import {
  getUploadUrl,
  listInstructions,
  getInstruction,
  createInstruction,
  createInstructionModule,
  updateInstruction,
  updateInstructionModule,
  deleteInstruction,
  confirmInstruction,
  listConfirmations,
  listMyConfirmations,
  Instruction,
  InstructionWithFile,
  InstructionPayload,
  InstructionModulePayload,
  InstructionConfirmation,
  ConfirmPayload,
  UploadUrlResponse,
} from '@training24/course/src/instruction'

interface InstructionState {
  all: Instruction[]
  allLoaded: boolean
  confirmationsByInstruction: Record<string, InstructionConfirmation[]>
  myConfirmations: InstructionConfirmation[]
  myConfirmationsLoaded: boolean
}

export type {
  Instruction,
  InstructionWithFile,
  InstructionPayload,
  InstructionModulePayload,
  InstructionConfirmation,
  ConfirmPayload,
  UploadUrlResponse,
}

export default {
  namespaced: true,

  state: (): InstructionState => ({
    all: [],
    allLoaded: false,
    confirmationsByInstruction: {},
    myConfirmations: [],
    myConfirmationsLoaded: false,
  }),

  getters: {
    isAllLoaded: (state: InstructionState): boolean => state.allLoaded,
    getAll: (state: InstructionState): Instruction[] => state.all,
    getConfirmations: (state: InstructionState) => (instructionId: string): InstructionConfirmation[] =>
      state.confirmationsByInstruction[instructionId] || [],
    // Bestätigung des aktuellen Mitarbeiters zu einer Unterweisung (falls vorhanden)
    getMyConfirmation: (state: InstructionState) => (instructionId: string): InstructionConfirmation | undefined =>
      state.myConfirmations.find((c) => c.instructionId === instructionId),
  },

  actions: {
    async LOAD_ALL(context: any, opts?: { force?: boolean }) {
      if (context.state.allLoaded && !(opts && opts.force)) return
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const items = await listInstructions(orgId, jwt)
      context.commit('SET_ALL', items)
    },

    async SHOW(context: any, instructionId: string): Promise<InstructionWithFile> {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      return getInstruction(orgId, instructionId, jwt)
    },

    async GET_UPLOAD_URL(
      context: any,
      { fileName, contentType }: { fileName: string; contentType: string }
    ): Promise<UploadUrlResponse> {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      return getUploadUrl(orgId, fileName, contentType, jwt)
    },

    async CREATE(context: any, payload: InstructionPayload) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await createInstruction(orgId, payload, jwt)
      context.commit('ADD', item)
      return item
    },

    async UPDATE(context: any, { instructionId, payload }: { instructionId: string; payload: Partial<InstructionPayload> }) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await updateInstruction(orgId, instructionId, payload, jwt)
      context.commit('REPLACE', item)
      return item
    },

    // Modul (Container aus Bausteinen) anlegen/ändern.
    async CREATE_MODULE(context: any, payload: InstructionModulePayload) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await createInstructionModule(orgId, payload, jwt)
      context.commit('ADD', item)
      return item
    },

    async UPDATE_MODULE(context: any, { instructionId, payload }: { instructionId: string; payload: Partial<InstructionModulePayload> }) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await updateInstructionModule(orgId, instructionId, payload, jwt)
      context.commit('REPLACE', item)
      return item
    },

    async DELETE(context: any, instructionId: string) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      await deleteInstruction(orgId, instructionId, jwt)
      context.commit('REMOVE', instructionId)
    },

    async LOAD_CONFIRMATIONS(context: any, instructionId: string) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const items = await listConfirmations(orgId, instructionId, jwt)
      context.commit('SET_CONFIRMATIONS', { instructionId, items })
      return items
    },

    async CONFIRM(context: any, { instructionId, payload }: { instructionId: string; payload: ConfirmPayload }) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await confirmInstruction(orgId, instructionId, payload, jwt)
      context.commit('ADD_MY_CONFIRMATION', item)
      return item
    },

    async LOAD_MY_CONFIRMATIONS(context: any, opts?: { force?: boolean }) {
      if (context.state.myConfirmationsLoaded && !(opts && opts.force)) return
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const items = await listMyConfirmations(orgId, jwt)
      context.commit('SET_MY_CONFIRMATIONS', items)
    },
  },

  mutations: {
    SET_ALL(state: InstructionState, items: Instruction[]) {
      state.all = items
      state.allLoaded = true
    },
    ADD(state: InstructionState, item: Instruction) {
      state.all = [...state.all, item]
    },
    REPLACE(state: InstructionState, item: Instruction) {
      state.all = state.all.map((i) => (i.instructionId === item.instructionId ? item : i))
    },
    REMOVE(state: InstructionState, instructionId: string) {
      state.all = state.all.filter((i) => i.instructionId !== instructionId)
    },
    SET_CONFIRMATIONS(state: InstructionState, { instructionId, items }: { instructionId: string; items: InstructionConfirmation[] }) {
      state.confirmationsByInstruction = { ...state.confirmationsByInstruction, [instructionId]: items }
    },
    SET_MY_CONFIRMATIONS(state: InstructionState, items: InstructionConfirmation[]) {
      state.myConfirmations = items
      state.myConfirmationsLoaded = true
    },
    ADD_MY_CONFIRMATION(state: InstructionState, item: InstructionConfirmation) {
      const others = state.myConfirmations.filter((c) => c.instructionId !== item.instructionId)
      state.myConfirmations = [...others, item]
    },
  },
}
